/**
 * Tier 3: Text-based signature tags for Word Online + Desktop.
 *
 * Inserts [[sig:email|name]] tags at the cursor position.
 * These tags are scanned by the backend after DOCX→PDF conversion
 * to determine signature field positions.
 */

export interface SignaturePlacement {
  email: string
  name: string
  /** 1-indexed page number. 0 = "let backend detect from PDF position" */
  page: number
  /** X position as percentage (0-100) */
  x: number
  /** Y position as percentage (0-100) */
  y: number
  /** Width as percentage (0-100) */
  width: number
  /** Height as percentage (0-100) */
  height: number
}

const TAG_REGEX = /\[\[sig:([^|]+)\|([^\]]+)\]\]/g

/** Insert a [[sig:email|name]] tag at the current cursor position */
export async function insertSignatureTag(email: string, name: string): Promise<void> {
  await Word.run(async (context) => {
    const selection = context.document.getSelection()
    selection.insertText(
      `[[sig:${email}|${name}]]`,
      Word.InsertLocation.replace,
    )
    await context.sync()
  })
}

/** Read all [[sig:...]] tags from the document body */
export async function readSignatureTags(): Promise<SignaturePlacement[]> {
  return await Word.run(async (context) => {
    const body = context.document.body
    body.load('text')
    await context.sync()

    const placements: SignaturePlacement[] = []
    let match: RegExpExecArray | null

    // Reset regex state
    TAG_REGEX.lastIndex = 0

    while ((match = TAG_REGEX.exec(body.text)) !== null) {
      placements.push({
        email: match[1],
        name: match[2],
        // page=0 signals backend to detect position from PDF
        page: 0,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      })
    }

    return placements
  })
}

/** Remove all [[sig:...]] tags from the document */
export async function removeSignatureTags(): Promise<void> {
  await Word.run(async (context) => {
    // Search for the tag pattern
    // Word search wildcards: [[ and ]] are literal, * matches anything
    const results = context.document.body.search('\\[\\[sig:*\\]\\]', {
      matchWildcards: true,
    })
    results.load('items')
    await context.sync()

    for (const range of results.items) {
      range.delete()
    }
    await context.sync()
  })
}
